import React, { useState, useCallback } from "react";
import { Button } from "antd";
import { Modal } from "react-bootstrap";
import { SendOutlined } from "@ant-design/icons";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import "./modal.css";

import RadioButtonGroup from "../RadioButtonGroup";
import { NextButton } from "./styledComponents";
import { useSetActionReasonMutation } from "../../../api/graphql";
import { ActionReason } from "../../../api/graphql/schema.generated";
import {
  INCOMING_TRANSACTION_REASONS,
  OUTGOING_TRANSACTION_REASONS,
  actionReasonLabelKey,
} from "../../pages/TransactionView/constants";

// Tax Ops reference holding all 22 quicktexts with full German copy, subjects,
// and when-to-use guidance. Interim stand-in until the in-modal preview
// (SMT-1192) lands. Applies to both the Transaction and Verification views.
const TEMPLATE_REFERENCE_URL =
  "https://smartasteuern.atlassian.net/wiki/spaces/TAX/pages/1820885006/ContactUser-TransactionView";

export const ContactUserModal = ({
  show,
  onHide,
  name,
  amount,
  transactionId,
  onAfterSend,
}: {
  show: boolean;
  onHide: () => void;
  name: string;
  amount: number;
  transactionId: string;
  /**
   * Runs after the action reason was saved. Each view decides what "after" means:
   * the Transaction View jumps to the next transaction in its queue, the
   * Verification View refreshes the affected row and stays where it is.
   */
  onAfterSend?: () => void | Promise<void>;
}) => {
  const { t } = useTranslation("ContactUserModal");
  const [reason, setReason] = useState<ActionReason | null>(null);
  const [showFinalModal, setShowFinalModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const [setActionReason] = useSetActionReasonMutation();

  const onNextClickHandler = () => {
    setShowFinalModal(true);
  };

  const onSendClickHandler = useCallback(async () => {
    if (reason) {
      setIsLoading(true);
      try {
        await setActionReason({
          variables: { reason, transactionId },
        });
      } catch (err) {
        notification.error({
          message: t("error", { message: (err as Error).message }),
        });
        setIsLoading(false);
        onHide();
        return;
      }

      // The reason is saved from here on, so the email is already on its way.
      // Confirm the send now, before the refresh: a failing refresh must stay a
      // warning and never suppress or contradict this success, or the operator
      // retries and the customer receives a second email.
      notification.success({ message: t("success", { name }) });

      try {
        await onAfterSend?.();
      } catch (err) {
        notification.warning({
          message: t("refreshWarning", { message: (err as Error).message }),
        });
      } finally {
        setIsLoading(false);
      }
    }
    onHide();
  }, [setActionReason, reason, transactionId, name, onHide, onAfterSend, t]);

  const onGoBackClickHandler = () => {
    setShowFinalModal(false);
  };

  const renderFinalModal = () => {
    const label = reason ? t(actionReasonLabelKey(reason)) : null;

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title className="contact-user-modal-title">
            {t("sendTitle", { name })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("reasonLabel")}</p>
          <b>{label}</b>
        </Modal.Body>
        <Modal.Footer>
          <Button
            data-test="contacUserGoBackBtn"
            onClick={onGoBackClickHandler}
          >
            {t("back")}
          </Button>
          <NextButton
            disabled={isLoading}
            data-test="contacUserSendBtn"
            onClick={onSendClickHandler}
          >
            <SendOutlined />
            {t("send")}
          </NextButton>
        </Modal.Footer>
      </>
    );
  };

  const renderReasonsModal = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title className="contact-user-modal-title">
          {t("title", { name })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="contact-user-modal-reason-header">
          <b>{t("chooseReason")}</b>
          <a
            href={TEMPLATE_REFERENCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-test="contacUserTemplatesLink"
          >
            {t("viewTemplates")}
          </a>
        </div>
        <RadioButtonGroup
          value={reason}
          options={(amount > 0
            ? INCOMING_TRANSACTION_REASONS
            : OUTGOING_TRANSACTION_REASONS
          ).map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
          onChange={(value) => setReason(value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button data-test="contacUserCancelBtn" onClick={onHide}>
          {t("cancel")}
        </Button>
        <NextButton
          disabled={!reason}
          data-test="contacUserNextBtn"
          onClick={onNextClickHandler}
        >
          {t("next")}
        </NextButton>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="contact-user-modal"
      data-test="сontactUserModal"
    >
      {showFinalModal ? renderFinalModal() : renderReasonsModal()}
    </Modal>
  );
};
